import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { grabsApi, type BlocklistEntry } from "@/api/grabs";
import type { MediaKind } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

/**
 * Reusable "Blocklist" section for media detail pages.
 *
 * The auto-grab flow (issue #4) writes to `grab_blocklist` whenever a
 * release fails — par2 unrecoverable, downloader rejected, articles
 * missing on the news server. The blocklist is normally invisible:
 * the next auto-retry just picks something else. But occasionally the
 * user wants to:
 *
 *   - See WHICH releases got blocklisted and why
 *   - Un-blocklist one entry (the indexer's flaky, the release is
 *     actually fine, retry from-scratch)
 *   - Wipe the whole blocklist for this item (start over after an
 *     indexer outage flooded the list)
 *
 * Renders a collapsed-by-default header showing the count, expanding
 * into a per-row table with delete buttons + a "Clear all" CTA.
 *
 * Drop into MovieDetail / SeriesDetail / AlbumDetail — three lines.
 */
export function BlocklistSection({
  mediaKind,
  mediaId,
}: {
  mediaKind: MediaKind;
  mediaId: number;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);

  // The list is small per-item (capped to retry attempts). Cheap to fetch
  // every time the section is expanded.
  const { data, isLoading } = useQuery({
    queryKey: ["blocklist", mediaKind, mediaId],
    queryFn: () => grabsApi.blocklist.list({ media_kind: mediaKind, media_id: mediaId, limit: 100 }),
  });
  const entries = data ?? [];

  const removeMut = useMutation({
    mutationFn: (id: number) => grabsApi.blocklist.remove(id),
    onSuccess: () => {
      toast("Blocklist entry removed", "success");
      qc.invalidateQueries({ queryKey: ["blocklist", mediaKind, mediaId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const clearMut = useMutation({
    mutationFn: () => grabsApi.blocklist.clearForMedia(mediaKind, mediaId),
    onSuccess: ({ removed }) => {
      toast(
        `Cleared ${removed} blocklist entr${removed === 1 ? "y" : "ies"}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["blocklist", mediaKind, mediaId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Clear failed", "error"),
  });

  // Don't render anything when the list is empty AND we're not loading.
  // The blocklist is a "you'll only see this if something went wrong"
  // surface; rendering an empty section all the time is noise.
  if (!isLoading && entries.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="eyebrow">Blocklist</h2>
          {entries.length > 0 && (
            <Status variant="chip" tone="warn">
              {entries.length} blocked
            </Status>
          )}
        </div>
        <div className="flex items-center gap-2">
          {entries.length > 0 && expanded && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              loading={clearMut.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Clear all ${entries.length} blocklist entries for this item? Future auto-grabs will be free to pick any of these releases again.`,
                  )
                ) {
                  clearMut.mutate();
                }
              }}
            >
              Clear all
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            disabled={isLoading || entries.length === 0}
          >
            {expanded ? "Hide" : "Show"}
          </Button>
        </div>
      </div>

      {expanded && (
        <p className="text-xs leading-relaxed text-foreground/55">
          Releases here failed to import (downloader rejected, par2
          unrecoverable, articles missing) and are excluded from future auto-
          grabs. Removing an entry lets the next search pick that release
          again — useful when the underlying issue (indexer outage, news
          server hiccup) has resolved.
        </p>
      )}

      {expanded && (
        <div className="overflow-hidden rounded-md border border-line bg-surface/30">
          <table className="w-full text-xs">
            <thead className="bg-surface-2/50">
              <tr className="text-left">
                <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Release GUID
                </th>
                <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Reason
                </th>
                <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Blocked at
                </th>
                <th className="w-20 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {entries.map((entry) => (
                <BlocklistRow
                  key={entry.id}
                  entry={entry}
                  removing={
                    removeMut.isPending && removeMut.variables === entry.id
                  }
                  onRemove={() => removeMut.mutate(entry.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BlocklistRow({
  entry,
  removing,
  onRemove,
}: {
  entry: BlocklistEntry;
  removing: boolean;
  onRemove: () => void;
}) {
  return (
    <tr className="transition hover:bg-surface-2/40">
      <td
        className="max-w-md truncate px-3 py-2 font-mono text-[11px] text-foreground/85"
        title={entry.release_guid}
      >
        {entry.release_guid}
      </td>
      <td className="px-3 py-2 text-foreground/65">{entry.reason ?? "—"}</td>
      <td className="px-3 py-2 font-mono text-foreground/55">
        {new Date(entry.blocked_at).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          loading={removing}
          onClick={onRemove}
          title="Remove this entry — the next auto-grab can pick this release again"
        >
          Remove
        </Button>
      </td>
    </tr>
  );
}
